import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    IconButton,
    Select,
    Tooltip,
    useClipboard,
} from '@chakra-ui/react';

import { CopyIcon } from '@/components/v4/Icon/CopyIcon';
import {
    blockSizes as allSizes,
    ERROR,
    getErrorByType,
    getValidationColors,
} from 'pages/telephony/numberBlocks/add/constants';

import CustomPhoneInput from 'pages/tpm/PhoneInput';
import { useSelector } from 'react-redux';
import { calculateLastNumber } from 'utils/numberBlockUtils.js';
import { _renderStringAsFlag } from 'pages/tpm';

const EditableBlockSizeCell = ({ getValue, row, column, table }) => {
    const initValue = getValue();
    const { darkMode } = useSelector((state) => state.settings);
    const {
        updateData,
        focusedRow,
        setFocusedRow,
        companyIsTrial,
        isCreateOrderWholesaler,
        isTableDisabled,
    } = table.options.meta;
    const [value, setValue] = useState(initValue);
    const selectRef = useRef(null);
    const blockSizes = useMemo(() => {
        if (isCreateOrderWholesaler) {
            return [{ value: 1, label: '1' }];
        }
        if (companyIsTrial === null) {
            return allSizes; // No company selected - all options
        }
        if (companyIsTrial === true) {
            return [{ value: 5, label: '5' }]; // Trial company - only 5
        }
        // Non-trial company - exclude 5
        return allSizes.filter((size) => size.value !== 5);
    }, [companyIsTrial, isCreateOrderWholesaler]);

    const onBlur = () => {
        if (isTableDisabled) return;
        if (value !== initValue) {
            updateData(row.index, column.id, value);
        }
    };

    const onChange = (e) => {
        if (isTableDisabled) return;
        const newValue = parseInt(e.target.value);
        setValue(newValue);
        updateData(row.index, column.id, newValue);
        setFocusedRow(row.index, 'number');
    };

    const onKeyDown = (e) => {
        if (isTableDisabled) return;
        if (e.key === 'Enter') {
            e.preventDefault();
            onBlur();
            setFocusedRow(row.index, 'number');
        }
    };

    useEffect(() => {
        if (row.index === focusedRow.row && focusedRow.column === 'blockSize') {
            selectRef?.current?.focus();
        }
    }, [focusedRow, row.index]);

    useEffect(() => {
        setValue(initValue);
    }, [initValue]);

    // Side effect that auto-correct block size when available options change
    useEffect(() => {
        const isValidSize = blockSizes.some((size) => size.value === value);
        if (!isValidSize && blockSizes.length > 0) {
            const newValue = blockSizes[0].value;
            setValue(newValue);
            updateData(row.index, column.id, newValue);
        }
    }, [blockSizes, value, updateData, row.index, column.id]);

    return (
        <Select
            ref={selectRef}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            size="sm"
            borderRadius="md"
            borderColor={darkMode ? 'dark.tcap-borderGray' : 'gray.200'}
            backgroundColor={darkMode ? 'dark.tcap' : null}
            color={darkMode ? 'dark.textDark' : null}
            isDisabled={isTableDisabled || isCreateOrderWholesaler}>
            {blockSizes?.map((size) => (
                <option
                    key={size.value}
                    value={size.value}
                    style={
                        darkMode
                            ? { background: '#2D3748', color: '#F7FAFC' }
                            : {}
                    }>
                    {size.label || size.name}
                </option>
            ))}
        </Select>
    );
};

const EditableNumberCell = ({ getValue, row, column, table }) => {
    const initValue = getValue();
    const { darkMode } = useSelector((state) => state.settings);
    const {
        updateData,
        focusedRow,
        setFocusedRow,
        setCopy,
        copy,
        duplicates,
        isAddingRowRef,
        warnings,
        isTableDisabled,
    } = table.options.meta;
    const [value, setValue] = useState(initValue);
    // Derived from row data: rows are keyed by position, so local state would follow the wrong row
    const countryIso2 = row.original.countryIso2 || null;
    const inputRef = useRef(null);
    const pendingSelectionRef = useRef(null);

    const onFocus = () => {
        if (isTableDisabled) return;
        setFocusedRow(row.original.id, 'number');
    };

    const onBlur = () => {
        if (isTableDisabled) return;
        // Skip blur handling when adding a new row (Enter key or AddIcon click)
        if (isAddingRowRef?.current) return;
        setFocusedRow(-1, null);
        if (value !== initValue) {
            updateData(row.index, column.id, value);
        }
    };

    const addNewRow = () => {
        if (isTableDisabled) return;
        updateData(row.index, column.id, value, true);
        setFocusedRow(0, 'number');
    };

    const onKeyDown = (e) => {
        if (isTableDisabled) return;
        // Capture selection before edit so caret restoration stays accurate
        if (inputRef.current) {
            pendingSelectionRef.current = {
                start: inputRef.current.selectionStart,
                end: inputRef.current.selectionEnd,
                // ADO: 3071
                valueLength: inputRef.current.value.length,
            };
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!value) return;
            addNewRow();
        }
        if (
            e.key === 'Delete' &&
            value === '' &&
            table.options.data?.length > 1
        ) {
            e.preventDefault();
            const newIndex = row.index > 0 ? row.index - 1 : 0;
            table.options.meta.deleteRow(row.index);
            setFocusedRow(newIndex, 'number');
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
            setCopy(true);
        }
    };

    // Compute validation info for tooltip and styling
    const getValidationInfo = () => {
        const rowId = row.original.id;

        if (duplicates.some((dup) => dup.id === rowId)) {
            return {
                message: ERROR.DUPLICATE.message,
                type: ERROR.DUPLICATE.severity,
            };
        }

        const rowWarning = warnings?.find((warn) => warn.id === rowId);
        if (rowWarning) {
            const error = getErrorByType(rowWarning.type);
            if (error) {
                return { message: error.message, type: error.severity };
            }
        }
        return null;
    };

    const validationInfo = getValidationInfo();
    const isWarning = validationInfo?.type === 'warning';
    const isError = validationInfo?.type === 'error';
    const isInvalid = isWarning || isError;
    const isFocused =
        focusedRow.row === row.original.id && focusedRow.column === 'number';
    const showTooltip = validationInfo && !isFocused;

    useEffect(() => {
        if (row.index === focusedRow.row && focusedRow.column === 'number') {
            if (document.activeElement === inputRef.current) return;
            setTimeout(() => {
                if (
                    inputRef.current &&
                    document.activeElement !== inputRef.current
                ) {
                    inputRef.current.focus();
                }
            }, 0);
        }
    }, [focusedRow, row.index]);

    useEffect(() => {
        setValue(initValue);
    }, [initValue]);

    const handlePaste = (event) => {
        if (isTableDisabled) return;
        const numberData = event.clipboardData.getData('text');
        if (numberData.length > 0) {
            setCopy(true);
        }
    };

    const handleChange = (phoneValue, countryData) => {
        if (isTableDisabled) return;
        const selection = pendingSelectionRef.current;
        const newValue = phoneValue ? `+${phoneValue}` : '';
        const newIso2 = countryData?.countryCode?.toUpperCase() || null;
        const countryChanged =
            Boolean(newIso2) && Boolean(countryIso2) && newIso2 !== countryIso2;

        if (copy && newValue.length > 0) {
            // Handle paste operation for multiple numbers
            const inputData = newValue.trim();
            if (inputData) {
                // Split by newlines only (not commas - commas may be formatting within a number)
                const splitNumbers = inputData
                    .split(/[\n\r]+/)
                    .map((num) => num.replace(/[^\d]/g, '').slice(0, 19))
                    .filter((num) => num.length > 0);

                if (splitNumbers.length > 1) {
                    // Handle bulk paste
                    table.options.meta.handleBulkPaste(
                        splitNumbers,
                        row.original.blockSize,
                    );
                    setCopy(false);
                    return;
                }
            }
        }

        setValue(newValue);
        updateData(row.index, column.id, newValue);
        updateData(row.index, 'countryIso2', newIso2);

        const detectedCountry = countryData?.countryCode ?? '';
        if (detectedCountry) {
            updateData(row.index, 'countryCode', detectedCountry);
        }

        if (newValue.length > 0 && inputRef.current) {
            setTimeout(() => {
                const input = inputRef.current;
                if (!input || document.activeElement !== input) return;
                const length = input.value.length;
                if (countryChanged) {
                    input.setSelectionRange(length, length);
                } else if (selection) {
                    // ADO: 3071
                    const target =
                        selection.end + (length - selection.valueLength);
                    const clamped = Math.max(0, Math.min(target, length));
                    input.setSelectionRange(clamped, clamped);
                }
            }, 0);
        }

        setCopy(false);
    };

    // Colors for warning vs error
    const { bgColor, borderColor, focusBorderColor, textColor } =
        getValidationColors(isInvalid, isWarning, darkMode);

    return (
        <Tooltip
            label={validationInfo?.message}
            isDisabled={!showTooltip}
            hasArrow
            placement="top"
            bg={isWarning ? 'yellow.400' : 'red.500'}
            color={isWarning ? 'gray.800' : 'white'}>
            <Box
                style={{ width: '100%' }}
                onPaste={handlePaste}
                sx={{
                    '& .react-tel-input .selected-flag:hover, .selected-flag:focus':
                        {
                            backgroundColor: 'transparent',
                            cursor: 'auto',
                        },
                    '& .react-tel-input .form-control:focus': {
                        boxShadow: `0 0 0 1px ${focusBorderColor}`,
                        borderColor: `${focusBorderColor}!important`,
                    },
                    '& .react-tel-input .selected-flag': {
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '12px',
                    },
                }}>
                <CustomPhoneInput
                    country=""
                    onlyCountries={undefined}
                    name="firstNumber"
                    value={value}
                    onChange={handleChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isTableDisabled}
                    disableDropdown
                    autoFormat={false}
                    renderStringAsFlag={_renderStringAsFlag(value, countryIso2)}
                    inputProps={{
                        ref: inputRef,
                        onKeyDown: onKeyDown,
                        'aria-label': `number-input-${row.index}`,
                        maxLength: 20,
                    }}
                    placeholder="Enter number in E.164 format"
                    inputStyle={{
                        width: '100%',
                        borderRadius: '0.375rem',
                        border: `1px solid ${borderColor}`,
                        backgroundColor: bgColor,
                        color: textColor,
                        cursor: isTableDisabled ? 'not-allowed' : 'text',
                        fontSize: '0.875rem',
                        paddingRight: '2rem',
                        height: '32px',
                    }}
                    buttonStyle={{
                        border: 'none',
                        borderRadius: '0.375rem 0 0 0.375rem',
                        backgroundColor: 'transparent',
                    }}
                    dropdownStyle={{
                        backgroundColor: darkMode ? '#2D3748' : 'white',
                        color: darkMode ? 'white' : 'black',
                    }}
                    containerStyle={{
                        width: '100%',
                    }}
                />
            </Box>
        </Tooltip>
    );
};

const LastNumberCell = ({ row, table }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { duplicates, warnings } = table.options.meta;
    const firstNumber = row.original.number;
    const blockSize = row.original.blockSize;
    const lastNumber = calculateLastNumber(firstNumber, blockSize);
    const countryIso2 = row.original.countryIso2 || null;

    const lastNumberWrapperProps = {
        '& .react-tel-input .selected-flag': {
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '12px',
        },
    };

    // Compute validation info for tooltip and styling
    const getValidationInfo = () => {
        const rowId = row.original.id;

        if (duplicates.some((dup) => dup.id === rowId)) {
            return { type: ERROR.DUPLICATE.severity };
        }

        const rowWarning = warnings?.find((warn) => warn.id === rowId);
        if (rowWarning) {
            const error = getErrorByType(rowWarning.type);
            if (error) {
                return { type: error.severity };
            }
        }
        return null;
    };

    const validationInfo = getValidationInfo();
    const isWarning = validationInfo?.type === 'warning';
    const isError = validationInfo?.type === 'error';
    const isInvalid = isWarning || isError;

    const { bgColor, borderColor, textColor } = getValidationColors(
        isInvalid,
        isWarning,
        darkMode,
        { muted: true },
    );

    return (
        <Box style={{ width: '100%' }} sx={lastNumberWrapperProps}>
            <CustomPhoneInput
                country=""
                value={lastNumber}
                onlyCountries={undefined}
                disabled
                disableDropdown
                isReadOnly
                type="tel"
                size="sm"
                name="lastNumber"
                placeholder="Last number in E.164 format"
                autoFormat={false}
                renderStringAsFlag={_renderStringAsFlag(
                    lastNumber,
                    countryIso2,
                )}
                borderColor={darkMode ? 'dark.tcap-borderGray' : 'gray.200'}
                borderRadius="md"
                cursor="not-allowed"
                backgroundColor={darkMode ? 'dark.tcap' : null}
                inputStyle={{
                    width: '100%',
                    borderRadius: '0.375rem',
                    border: `1px solid ${borderColor}`,
                    backgroundColor: bgColor,
                    color: textColor,
                    fontSize: '0.875rem',
                    paddingRight: '2rem',
                    height: '32px',
                }}
                buttonStyle={{
                    border: 'none',
                    borderRadius: '0.375rem 0 0 0.375rem',
                    backgroundColor: 'transparent',
                }}
                dropdownStyle={{
                    backgroundColor: darkMode ? '#2D3748' : 'white',
                    color: darkMode ? 'white' : 'black',
                }}
                containerStyle={{
                    width: '100%',
                }}
            />
        </Box>
    );
};

const CopyNumberCell = ({ row }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { hasCopied, onCopy } = useClipboard(row.original.number || '');

    return (
        <Tooltip label={hasCopied ? 'Copied!' : 'Copy number'} hasArrow>
            <IconButton
                size="sm"
                rounded="full"
                variant="ghost"
                icon={<CopyIcon boxSize="14px" />}
                onClick={onCopy}
                isDisabled={!row.original.number}
                _hover={{
                    bg: darkMode ? 'dark.hoverGray' : 'gray.100',
                    color: 'brand.500',
                }}
                _focus={{ boxShadow: 'none' }}
            />
        </Tooltip>
    );
};

export {
    CopyNumberCell,
    EditableBlockSizeCell,
    EditableNumberCell,
    LastNumberCell,
};
